import {
  ACCOUNT_CONTRACTOR_TOPCLASS,
  ACCOUNT_LABEL_TABLE_NAME,
  getClientId,
  getReadId,
  getTableClientId,
  Language,
} from '..';

import { AccountLabel, CustomValue } from './model-app';

export enum EOEntityToSynchronize {
  BANK_ACCOUNTS = 'bankAccounts',
  CONTACTS = 'contacts',
  POSTINGS = 'postings',
  RECONCILIATIONS = 'reconciliations',
}

export interface LightEOGLAccount {
  ID: string;
  Code: string;
  Description: string;
  Division: number;
  VATNonDeductiblePercentage?: number | null;
}

export interface LightEOBankEntry {
  EntryID: string;
  BankEntryLines: LightEOBankEntryLine[];
  Division: number;
  JournalCode: string | null;
}

export interface LightEOBankEntryLine {
  ID: string;
  Account: string | null;
  AccountName: string | null;
  AmountFC: number | null;
  Date: string | null;
  Division: number;
  EntryID: string;
  GLAccount: string | null;
  CostCenter: string | null;
  Notes: string | null;
  VATCode: string | null;
  Description: string | null;
}

export interface LightEOBankEntryWithBeneficiaries extends Omit<LightEOBankEntry, 'BankEntryLines'> {
  BankEntryLines: LightEOBankEntryLineWithBeneficiaries[];
}

export interface LightEOBankEntryLineWithBeneficiaries extends LightEOBankEntryLine {
  counterpartAccountNumber?: string;
  counterpartName?: string;
}

export interface EOBankEntryLine extends LightEOBankEntryLine {
  AccountCode: string | null;
  AmountDC: number | null;
  AmountVATFC: number | null;
  Asset: string | null;
  AssetCode: string | null;
  AssetDescription: string | null;
  CostCenterDescription: string | null;
  CostUnit: string | null;
  CostUnitDescription: string | null;
  Created: string | null;
  Creator: string | null;
  CreatorFullName: string | null;
  CustomField: string | null;
  Document: string | null;
  DocumentNumber: number | null;
  DocumentSubject: string | null;
  EntryNumber: number | null;
  ExchangeRate: number | null;
  GLAccountCode: string | null;
  GLAccountDescription: string | null;
  LineNumber: number | null;
  Modified: string | null;
  Modifier: string | null;
  ModifierFullName: string | null;
  OffsetID: string | null;
  OurRef: number | null;
  Project: string | null;
  ProjectCode: string | null;
  ProjectDescription: string | null;
  Quantity: number | null;
  VATCodeDescription: string | null;
  VATPercentage: number | null;
  VATType: string | null;
}

export interface LightEODivision {
  Code: number;
  Description: string;
}

export interface LightEOVatCode {
  ID: string;
  Country: string | null;
  Description: string;
  Code: string;
  Division: number;
  Percentage: number;
  Charged?: boolean | null;
  IntraStat?: boolean | null;
  Type?: string | null;
}

export interface LightEOCostcenter {
  ID: string;
  Code: string;
  Description: string;
}

export enum EOJournalType {
  CASH = 10,
  BANK = 12,
  PAYMENT_SERVICE = 16,
  SALES = 20,
  RETURN_INVOICE = 21,
  PURCHASE = 22,
  RECEIVED_RETURN_INVOICE = 23,
  GENERAL_JOURNAL = 90,
}

export interface EOJournal {
  ID: string;
  AllowVariableCurrency?: boolean | null;
  AllowVariableExchangeRate?: boolean | null;
  AllowVAT?: boolean | null;
  AutoSave?: boolean | null;
  Bank?: string | null;
  BankAccountBICCode?: string | null;
  BankAccountCountry?: string | null;
  BankAccountDescription?: string | null;
  BankAccountIBAN?: string | null;
  BankAccountID?: string | null;
  BankAccountIncludingMask?: string | null;
  BankAccountUseSEPA?: boolean | null;
  BankAccountUseSepaDirectDebit?: boolean | null;
  BankName?: string | null;
  Code?: string | null;
  Created?: string | null; // ISO 8601 format (e.g. '2023-01-01T12:00:00Z')
  Creator?: string | null;
  CreatorFullName?: string | null;
  Currency?: string | null;
  CurrencyDescription?: string | null;
  CustomField?: string | null;
  Description?: string | null;
  Division?: number | null;
  GLAccount?: string | null;
  GLAccountCode?: string | null;
  GLAccountDescription?: string | null;
  GLAccountType?: number | null;
  IsBlocked?: boolean | null;
  Modified?: string | null;
  Modifier?: string | null;
  ModifierFullName?: string | null;
  PaymentInTransitAccount?: string | null;
  PaymentServiceAccountIdentifier?: string | null;
  PaymentServiceProvider?: number | null;
  PaymentServiceProviderName?: string | null;
  Type?: EOJournalType | null;
}

export enum EOAccountStatus {
  CUSTOMER = 'C',
  NONE = 'A',
  PROSPECT = 'P',
  SUSPECT = 'S',
}

export enum EOAccountType {
  RELATION = 'A',
  DIVISION = 'D',
}

export interface LightEOAccount {
  ID: string;
  Code: string;
  Division?: number;
  Email?: string | null;
  IsSupplier: boolean;
  Name: string;
  Phone: string | null;
  PhoneExtension?: string | null;
  Status?: EOAccountStatus | null;
  Type: EOAccountType;
  VATNumber?: string | null;
  Remarks?: string | null;
}

export interface LightEOAccountWithBankAccounts extends LightEOAccount {
  BankAccounts: LightEOBankAccount[];
}

export interface LightEOBankAccount {
  ID: string;
  BankAccount: string;
  Division: number;
  BICCode: string;
  BankName: string | null;
  BankAccountHolderName: string | null;
}

export interface LightEOSalesEntry {
  EntryID: string;
  InvoiceNumber: number;
  AmountFC: number;
  AmountDC: number;
  YourRef: string | null;
  Status: number;
  DueDate: string | null;
}

export enum EOEntityToFetch {
  ACCOUNTS = 'Accounts',
  BANK_ENTRIES = 'BankEntries',
  CUSTOMER_ACCOUNTS = 'CustomerAccounts',
  DIVISIONS = 'Divisions',
  COST_CENTERS = 'Costcenters',
  GLACCOUNTS = 'GLAccounts',
  JOURNALS = 'Journals',
  SALES_ENTRIES = 'SalesEntries',
  VAT_CODES = 'VATCodes',
}

export const ALL_POSSIBLE_EO_ENTITIES_TO_FETCH = [
  EOEntityToFetch.ACCOUNTS,
  EOEntityToFetch.BANK_ENTRIES,
  EOEntityToFetch.CUSTOMER_ACCOUNTS,
  EOEntityToFetch.DIVISIONS,
  EOEntityToFetch.COST_CENTERS,
  EOEntityToFetch.JOURNALS,
  EOEntityToFetch.GLACCOUNTS,
  EOEntityToFetch.SALES_ENTRIES,
  EOEntityToFetch.VAT_CODES,
];

export interface GenericExactOnlineMatching {
  exactOnlineCodeOrId?: string;
  exactOnlineDivision?: string;
  exactOnlineDisplayCode?: string;
  u2rId: string;
  u2rVersion: number;
  u2rCustomValues?: CustomValue[] | null;
}

export interface AccountLabelWithLightEOGLAccountMatching extends AccountLabel {
  lightEOGLAccountMatching?: LightEOGLAccount;
  shouldDisableMatching: boolean;
}

export const getFakeContractorAccountLabelId = (ownerId: string) => `FAKE-${ownerId}`;

export interface ExactOnlineMatchingCustomValues {
  division: string;
  displayCode: string;
  id: string;
}

export const getFakeContractorAccountLabel = (clientId: string, ownerId: string): AccountLabel => ({
  id: getFakeContractorAccountLabelId(ownerId),
  class: ACCOUNT_CONTRACTOR_TOPCLASS,
  topClass: ACCOUNT_CONTRACTOR_TOPCLASS,
  labels: [
    { language: Language.EN, label: 'Suppliers' },
    { language: Language.FR, label: 'Fournisseurs' },
    { language: Language.NL, label: 'Leverenciers' },
    { language: Language.DE, label: 'Lieferanten' },
  ],
  clientId: getTableClientId(clientId, ACCOUNT_LABEL_TABLE_NAME),
  readId: getReadId(getClientId(clientId), ACCOUNT_LABEL_TABLE_NAME),
});
